import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import JourneyProp from '../../../journey/propTypes/journeyProp';
import {
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_TYPE,
  normalizeJourney,
  TROPHY_CONFIG,
} from '../../../../services/utilityFunctions';
import { ENROLL_SOURCE } from '../../../../services/googleAnalytics';
import JourneyProgress from '../../../journey/overall/JourneyProgress';
import JourneyBannerNextStep from './JourneyBannerNextStep';
import JourneyBannerNextReward, { REWARD_TYPE } from './JourneyBannerNextReward';
import JourneyBannerAllRewards from './JourneyBannerAllRewards';
import TranslationContext from '../../../../contextProviders/TranslationContext';

const detailsLinkText = journey => {
  if (journey.progress === 0) {
    return 'start';
  }
  if (journey.progress > 0 && journey.progress < 100) {
    return 'continue';
  }
  return 'details';
};

const findNextStepIndex = (steps, allowTrophy) => {
  for (let i = steps.length - 1; i >= 1; i -= 1) {
    if (
      steps[i - 1].status === JOURNEY_STEP_STATUS.DONE ||
      steps[i - 1].status === JOURNEY_STEP_STATUS.IN_PROGRESS
    ) {
      if (!allowTrophy) {
        while (
          steps[i].type === JOURNEY_STEP_TYPE.TROPHY &&
          i < steps.length - 1
        ) {
          i += 1;
        }
      }
      return i;
    }
  }

  return -1;
};

const findNextStep = steps => {
  const index = findNextStepIndex(steps, false);
  return index !== -1 ? steps[index] : null;
};

const findNextReward = steps => {
  const index = findNextStepIndex(steps, true);
  if (index === -1) {
    return null;
  }

  for (let i = index; i < steps.length; i += 1) {
    if (steps[i].rewards) {
      const { rewards } = steps[i];

      if (rewards.coins) {
        if (rewards.coins.wallet > 0) {
          return { type: REWARD_TYPE.COINS, step: steps[i] };
        }
      }

      if (rewards.trophies) {
        const trophiesToDisplay = Object.entries(rewards.trophies)
          .filter(([trophy, value]) => !!value)
          .map(([trophy]) => trophy);
        if (trophiesToDisplay.length > 0) {
          return { type: REWARD_TYPE.TROPHY, step: steps[i] };
        }
      }

      if (rewards.coupons && rewards.coupons.length > 0) {
        return { type: REWARD_TYPE.COUPON, step: steps[i] };
      }

      if (rewards.vouchers && rewards.vouchers.length > 0) {
        return { type: REWARD_TYPE.VOUCHER, step: steps[i] };
      }
    }
  }

  return null;
};

export default function JourneyBanner({ t, journey: journeyParam, services }) {
  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
    'academy-journey-item',
  );

  const journey = normalizeJourney(services, journeyParam);

  const image = journey.image
    ? journey.image.thumb
    : '/images/no_image_upload.png';

  const nextStep = findNextStep(journey.steps);
  const nextReward = findNextReward(journey.steps);

  const rewards = journey.rewards || {};

  const coupons =
    rewards.coupons && rewards.coupons.length > 0
      ? {
          type: REWARD_TYPE.COUPON,
          items: (rewards.coupons || []).map(
            c => (c.image ? c.image.thumb : '/images/no_image_upload.png'),
          ),
        }
      : null;
  const vouchers =
    rewards.vouchers && rewards.vouchers.length > 0
      ? {
          type: REWARD_TYPE.VOUCHER,
          items: (rewards.vouchers || []).map(
            v => (v.logo ? v.logo.thumb : '/images/no_images_upload.png'),
          ),
        }
      : null;
  const coins =
    rewards.coins && rewards.coins.wallet > 0
      ? { type: REWARD_TYPE.COINS, coins: rewards.coins }
      : null;
  const allNonTrophies = coins || coupons || vouchers;

  const allTrophies = Object.entries(rewards.trophies || {})
    .filter(([trophy, value]) => !!value)
    .slice(0, 3)
    .map(([trophy]) => TROPHY_CONFIG[trophy].imageURL);

  return (
    <TranslationContext.Provider value={t}>
      <div className={containerClass}>
        <div className="row journey-container-mobile">
          <div className="col-lg-4 col-md-4 journey-basic-info">
            <div className="image-wrapper"><img src={image} alt="journey" /></div>
            <div className="journey-title-progress">
              <h6>{journey.title}</h6>
              {journey.progress > 0 ? (
                <div className="journey-progress-container">
                  <JourneyProgress t={t} progress={journey.progress} />
                </div>
              ) : (
                <>
                  <hr className="divider-black" />
                  <span className="journey-summary">{journey.summary}</span>
                </>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-md-5 journey-next">
            {journey.progress > 0 ? (
              <>
                {nextStep && <JourneyBannerNextStep t={t} step={nextStep} />}
                {nextReward && (
                  <JourneyBannerNextReward
                    type={nextReward.type}
                    step={nextReward.step}
                  />
                )}
              </>
            ) : (
              <div className="row col-lg-12">
                <div className="col-lg-8 col-12">
                  {allNonTrophies && (
                    <JourneyBannerAllRewards
                      title={t('journey_rewards')}
                      {...allNonTrophies}
                    />
                  )}
                </div>
                <div className="col-lg-4 col-12">
                  {allTrophies.length > 0 && (
                    <JourneyBannerAllRewards
                      title={t('journey_trophies')}
                      items={allTrophies}
                      type={REWARD_TYPE.TROPHY}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="col-lg-2 col-md-3 journey-actions">
            <a
              href={`/journey/${journey.slug}`}
              className="btn m-btn--pill journey-enroll"
            >
              {t(detailsLinkText(journey))}
            </a>
          </div>
        </div>
      </div>
    </TranslationContext.Provider>
  );
}

JourneyBanner.propTypes = {
  t: PropTypes.func.isRequired,
  journey: PropTypes.shape(JourneyProp.propType).isRequired,
  isLogged: PropTypes.bool,
  source: PropTypes.oneOf(Object.values(ENROLL_SOURCE)),
  services: PropTypes.shape({}),
};

JourneyBanner.defaultProps = {
  isLogged: true,
  source: ENROLL_SOURCE.ACADEMY,
  services: null,
};
